import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { buyersAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

const memberFormSchema = z.object({
  name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  document: z.string().min(1, 'CPF é obrigatório'),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
})

type MemberFormValues = z.infer<typeof memberFormSchema>

interface BuyerMemberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  buyerId: string
  member?: any
}

export function BuyerMemberDialog({
  open,
  onOpenChange,
  buyerId,
  member,
}: BuyerMemberDialogProps) {
  const queryClient = useQueryClient()
  const isEdit = !!member

  const form = useForm<MemberFormValues>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: member
      ? {
          name: member.name,
          document: member.document,
          email: member.email || '',
          phone: member.phone || '',
        }
      : {
          name: '',
          document: '',
          email: '',
          phone: '',
        },
  })

  const mutation = useMutation({
    mutationFn: async (values: MemberFormValues) => {
      const payload = {
        name: values.name,
        document: values.document.replace(/\D/g, ''),
        email: values.email || undefined,
        phone: values.phone || undefined,
      }
      if (isEdit) {
        return buyersAPI.updateMember(buyerId, member.id, payload)
      }
      return buyersAPI.addMember(buyerId, payload)
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Membro atualizado!' : 'Co-comprador adicionado!')
      queryClient.invalidateQueries({ queryKey: ['buyer', buyerId] })
      onOpenChange(false)
      form.reset()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar Co-comprador' : 'Adicionar Co-comprador'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Atualize as informações do co-comprador.'
              : 'Adicione uma pessoa ao grupo de compradores (cônjuge, sócio, etc).'}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="space-y-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="member-name">Nome *</Label>
              <Input
                id="member-name"
                {...form.register('name')}
                placeholder="Nome completo"
              />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive mt-1">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="member-document">CPF *</Label>
              <Input
                id="member-document"
                {...form.register('document')}
                placeholder="000.000.000-00"
              />
              {form.formState.errors.document && (
                <p className="text-sm text-destructive mt-1">
                  {form.formState.errors.document.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="member-email">E-mail</Label>
              <Input
                id="member-email"
                type="email"
                {...form.register('email')}
                placeholder="email@exemplo.com"
              />
            </div>

            <div>
              <Label htmlFor="member-phone">Telefone</Label>
              <Input
                id="member-phone"
                {...form.register('phone')}
                placeholder="(00) 00000-0000"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEdit ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
